// M12 — Branding API wrappers. PLAN §10.

import type {
  BrandingConfig,
  BrandingLogoCommitRequest,
  BrandingLogoPresignRequest,
  BrandingLogoPresignResponse,
  BrandingResponse,
} from "@mahir/shared";
import { api, apiRaw, uploadFile } from "./client";

// ─── Default brand ─────────────────────────────────────────
export async function getDefaultBranding(): Promise<BrandingResponse> {
  return api<BrandingResponse>("/admin/branding/default");
}

export async function updateDefaultBranding(
  body: BrandingConfig,
): Promise<{ config: BrandingConfig }> {
  return api<{ config: BrandingConfig }>("/admin/branding/default", { method: "PUT", body });
}

export async function presignDefaultLogo(
  body: BrandingLogoPresignRequest,
): Promise<BrandingLogoPresignResponse> {
  return api<BrandingLogoPresignResponse>("/admin/branding/default/logo/presign", {
    method: "POST",
    body,
  });
}

export async function commitDefaultLogo(
  body: BrandingLogoCommitRequest,
): Promise<{ logoStoragePath: string }> {
  return api<{ logoStoragePath: string }>("/admin/branding/default/logo/commit", {
    method: "POST",
    body,
  });
}

export async function deleteDefaultLogo(): Promise<void> {
  await api("/admin/branding/default/logo", { method: "DELETE" });
}

// ─── Per-client brand ──────────────────────────────────────
export async function getClientBranding(clientId: string): Promise<BrandingResponse> {
  return api<BrandingResponse>(`/admin/clients/${clientId}/branding`);
}

export async function updateClientBranding(
  clientId: string,
  body: BrandingConfig,
): Promise<{ config: BrandingConfig }> {
  return api<{ config: BrandingConfig }>(`/admin/clients/${clientId}/branding`, {
    method: "PUT",
    body,
  });
}

export async function disableClientBranding(clientId: string): Promise<void> {
  await api(`/admin/clients/${clientId}/branding`, { method: "DELETE" });
}

export async function presignClientLogo(
  clientId: string,
  body: BrandingLogoPresignRequest,
): Promise<BrandingLogoPresignResponse> {
  return api<BrandingLogoPresignResponse>(`/admin/clients/${clientId}/branding/logo/presign`, {
    method: "POST",
    body,
  });
}

export async function commitClientLogo(
  clientId: string,
  body: BrandingLogoCommitRequest,
): Promise<{ logoStoragePath: string }> {
  return api<{ logoStoragePath: string }>(`/admin/clients/${clientId}/branding/logo/commit`, {
    method: "POST",
    body,
  });
}

export async function deleteClientLogo(clientId: string): Promise<void> {
  await api(`/admin/clients/${clientId}/branding/logo`, { method: "DELETE" });
}

// ─── Logo upload helper (presign + PUT + commit) ───────────
export async function uploadLogoFlow(
  scope: { type: "default" } | { type: "client"; clientId: string },
  file: File,
): Promise<{ logoStoragePath: string }> {
  const presignReq: BrandingLogoPresignRequest = {
    fileName: file.name,
    mimeType: file.type === "image/png" ? "image/png" : "image/jpeg",
    sizeBytes: file.size,
  };
  const presigned =
    scope.type === "default"
      ? await presignDefaultLogo(presignReq)
      : await presignClientLogo(scope.clientId, presignReq);

  // Backend proxy PUT (CSRF token + auth cookie otomatik)
  await uploadFile(presigned.uploadUrl, file);

  const commitBody: BrandingLogoCommitRequest = { storagePath: presigned.storagePath };
  return scope.type === "default"
    ? commitDefaultLogo(commitBody)
    : commitClientLogo(scope.clientId, commitBody);
}

// apiRaw imported only to keep linter from complaining if not used elsewhere
void apiRaw;
